import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography, Divider, Collapse, Tooltip, Badge } from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FactoryIcon from "@mui/icons-material/Factory";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import BarChartIcon from "@mui/icons-material/BarChart";
import LabelIcon from "@mui/icons-material/Label";
import StoreIcon from "@mui/icons-material/Store";
import PersonIcon from "@mui/icons-material/Person";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import Logo from "../../Assets/images/logoMenu.svg";
import SelectUnidade from "../SelectUnidade";
import UserComponent from "../UserComponent";
import { FornecedorService } from "../../service/fornecedor_service";
import ColetaService from "../../service/coleta_service";
import { useUser } from "../../context/UserContext";
import { rotaLiberada, tipoDaLicenca } from "../../utils/licenca";

const navSections = [
	{
		title: "Principal",
		items: [
			{ to: "/home", label: "Dashboard", icon: DashboardIcon },
			{ to: "/solicitacoes", label: "Solicitações", icon: HowToRegIcon, badge: "fornecedor" },
			{ to: "/atualizacoes", label: "Atualizações", icon: NewReleasesIcon }
		],
	},
	{
		title: "Cadastros",
		items: [
			{ to: "/distribuidora", label: "Distribuidora", icon: StoreIcon },
			{ to: "/fornecedores", label: "Fornecedores", icon: PeopleIcon },
			{ to: "/clientes", label: "Clientes", icon: PersonIcon },
			{ to: "/usuarios", label: "Usuários", icon: ManageAccountsIcon },
		],
	},
	{
		title: "Movimentos",
		items: [
			{ to: "/compras", label: "Compras", icon: LocalShippingIcon },
			{ to: "/ordem-producao", label: "Ordem de Produção", icon: FactoryIcon },
			{ to: "/vendas", label: "Vendas", icon: ShoppingCartIcon },
			{ to: "/etiquetas", label: "Etiquetas", icon: LabelIcon },
		],
	},
	{
		title: "Gestão de Fornecedores",
		items: [
			{ to: "/coletas", label: "Coletas", icon: AssignmentIcon, badge: "retificacoes" },
		],
	},
	{
		title: "Consultas",
		items: [
			{ to: "/estoque", label: "Estoque", icon: WarehouseIcon },
			{ to: "/relatorios", label: "Relatórios", icon: BarChartIcon },
		],
	},
];

function AppSidebar({ currentPage, onClose, isDrawer = false, collapsed = false }) {
	const location = useLocation();
	const { user } = useUser();
	const [, setUnidadeNome] = useState("");
	const [expandedSections, setExpandedSections] = useState({});
	const [solicitacoesPendentes, setSolicitacoesPendentes] = useState(0);
	const [retificacoesNaoVistas, setRetificacoesNaoVistas] = useState(0);

	// O menu mostra só o que a licença da distribuidora abre. Seção que fica sem
	// item nenhum some junto, para não sobrar título vazio.
	const tipoLicenca = tipoDaLicenca(user);
	const secoesVisiveis = useMemo(
		() =>
			navSections
				.map((section) => ({
					...section,
					items: section.items.filter((item) => rotaLiberada(item.to, tipoLicenca)),
				}))
				.filter((section) => section.items.length > 0),
		[tipoLicenca]
	);

	// Todos os itens em lista plana para o modo colapsado
	const allNavItems = useMemo(
		() => secoesVisiveis.flatMap((s) => s.items),
		[secoesVisiveis]
	);

	// Caminho do item de menu que melhor casa com a rota atual (mais específico)
	const bestMatchPath = (() => {
		const currentPath = location.pathname;
		if (currentPath === "/home" || currentPath === "/") return "/home";
		const matches = allNavItems
			.filter((item) => item.to !== "/home" && currentPath.startsWith(item.to))
			.map((item) => item.to);
		if (matches.length === 0) return null;
		return matches.reduce((a, b) => (b.length > a.length ? b : a));
	})();

	// Expande apenas a seção que contém o item ativo
	useEffect(() => {
		const activeSection = secoesVisiveis.find((section) =>
			section.items.some((item) => item.to === bestMatchPath)
		);

		if (activeSection) {
			setExpandedSections((prev) => ({
				...prev,
				[activeSection.title]: true,
			}));
		}
	}, [bestMatchPath, secoesVisiveis]);

	// Conta as solicitações de fornecedor pendentes para exibir no badge
	useEffect(() => {
		const unidadeId = localStorage.getItem("selected_unidade_id");
		if (!unidadeId) return;
		const service = new FornecedorService();
		service
			.getSolicitacoes(unidadeId, "PENDENTE")
			.then((data) => setSolicitacoesPendentes(data?.length || 0))
			.catch(() => {});
	}, [location.pathname]);

	// Retificações que os fornecedores fizeram pelo WhatsApp e ninguém da unidade viu
	// ainda. Recarrega também quando a tela de Coletas marca como vistas: sem isso o
	// badge só sumiria na próxima navegação.
	useEffect(() => {
		const carregar = () => {
			const unidadeId = localStorage.getItem("selected_unidade_id");
			if (!unidadeId) return;
			new ColetaService()
				.getRetificacoesNaoVistas(unidadeId)
				.then((lista) => setRetificacoesNaoVistas(lista.length))
				.catch(() => {});
		};
		carregar();
		window.addEventListener("retificacoes-atualizadas", carregar);
		return () => window.removeEventListener("retificacoes-atualizadas", carregar);
	}, [location.pathname]);

	const badgeFor = (item) => {
		if (item.badge === "fornecedor") return solicitacoesPendentes;
		if (item.badge === "retificacoes") return retificacoesNaoVistas;
		return 0;
	};

	// O grupo soma os badges dos seus itens: com o grupo fechado, é o único sinal.
	const badgeDaSecao = (section) => section.items.reduce((soma, item) => soma + badgeFor(item), 0);

	useEffect(() => {
		const unidade = localStorage.getItem("selected_unidade_nome");
		if (unidade) {
			setUnidadeNome(unidade);
		}
	}, []);

	const toggleSection = (sectionTitle) => {
		setExpandedSections((prev) => ({
			...prev,
			[sectionTitle]: !prev[sectionTitle],
		}));
	};

	const isActive = (path) => path === bestMatchPath;

	const handleClick = () => {
		if (onClose) onClose();
	};

	const sidebarWidth = isDrawer ? "100%" : collapsed ? 64 : 260;

	return (
		<Box
			sx={{
				width: sidebarWidth,
				height: "100vh",
				bgcolor: "#1a9e8f",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
				transition: "width 0.25s ease",
				...(isDrawer ? {} : {
					position: "fixed",
					left: 0,
					top: 0,
				}),
			}}
		>
			{/* Header */}
			<Box
				sx={{
					p: collapsed ? 1.5 : 2.5,
					pb: collapsed ? 1.5 : 2,
					display: "flex",
					flexDirection: "column",
					alignItems: collapsed ? "center" : "flex-start",
					transition: "padding 0.25s ease",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: collapsed ? 0 : 2,
						mb: collapsed ? 0 : 2,
						justifyContent: collapsed ? "center" : "flex-start",
						width: "100%",
					}}
				>
					<Box
						sx={{
							width: 44,
							height: 44,
							borderRadius: "12px",
							bgcolor: "rgba(255,255,255,0.15)",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<img src={Logo} alt="Sisagri" style={{ width: 32, height: 32, objectFit: "contain" }} />
					</Box>

					{!collapsed && (
						<Box>
							<Typography variant="subtitle1" sx={{ fontWeight: 700, color: "white", lineHeight: 1.2 }}>
								Sisagri
							</Typography>
							<Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
								Distribuidor
							</Typography>
						</Box>
					)}
				</Box>

				{/* Unidade Selector — só no modo expandido */}
				{!collapsed && (
					<Box sx={{ mt: 1, width: "100%" }}>
						<SelectUnidade
							verticalAnchorOrigin="top"
							horizontalAnchorOrigin="right"
							verticalTransformOrigin="top"
							horizontalTransformOrigin="left"
							variant="sidebar"
						/>
					</Box>
				)}
			</Box>

			<Divider sx={{ bgcolor: "rgba(255,255,255,0.15)", mx: collapsed ? 1 : 2 }} />

			{/* Navigation */}
			<Box sx={{ flex: 1, overflow: "auto", py: 2, px: collapsed ? 0.75 : 1.5 }}>
				{collapsed ? (
					/* Modo colapsado: só ícones com tooltip */
					allNavItems.map((item) => {
						const active = isActive(item.to);
						return (
							<Tooltip key={item.to} title={item.label} placement="right" arrow>
								<Link to={item.to} onClick={handleClick} style={{ textDecoration: "none" }}>
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											width: 44,
											height: 44,
											mx: "auto",
											mb: 0.5,
											borderRadius: "10px",
											bgcolor: active ? "rgba(255,255,255,0.2)" : "transparent",
											color: active ? "white" : "rgba(255,255,255,0.8)",
											transition: "all 0.15s ease",
											"&:hover": {
												bgcolor: active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
											},
										}}
									>
										<Badge badgeContent={badgeFor(item)} color="warning" overlap="circular">
											<item.icon sx={{ fontSize: 22 }} />
										</Badge>
									</Box>
								</Link>
							</Tooltip>
						);
					})
				) : (
					/* Modo expandido: seções com collapse */
					secoesVisiveis.map((section) => {
						const isExpanded = expandedSections[section.title];
						return (
							<Box key={section.title} sx={{ mb: 1 }}>
								<Box
									onClick={() => toggleSection(section.title)}
									sx={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										px: 1.5,
										py: 0.75,
										cursor: "pointer",
										borderRadius: "8px",
										"&:hover": {
											bgcolor: "rgba(255,255,255,0.08)",
										},
									}}
								>
									<Typography
										variant="overline"
										sx={{
											color: "rgba(255,255,255,0.6)",
											fontSize: "0.65rem",
											fontWeight: 600,
											letterSpacing: "0.08em",
										}}
									>
										{section.title}
									</Typography>
									<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
										{badgeDaSecao(section) > 0 && (
											<Box
												sx={{
													minWidth: 18,
													height: 18,
													px: 0.6,
													borderRadius: "9px",
													bgcolor: "#f57c00",
													color: "white",
													fontSize: "0.65rem",
													fontWeight: 700,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}}
											>
												{badgeDaSecao(section)}
											</Box>
										)}
										{isExpanded ? (
											<ExpandLessIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)" }} />
										) : (
											<ExpandMoreIcon sx={{ fontSize: 18, color: "rgba(255,255,255,0.5)" }} />
										)}
									</Box>
								</Box>
								<Collapse in={isExpanded} timeout="auto">
									<Box sx={{ mt: 0.5 }}>
										{section.items.map((item) => {
											const active = isActive(item.to);
											return (
												<Link
													key={item.to}
													to={item.to}
													onClick={handleClick}
													style={{ textDecoration: "none" }}
												>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1.5,
															px: 1.5,
															py: 1,
															borderRadius: "10px",
															mb: 0.25,
															bgcolor: active ? "rgba(255,255,255,0.2)" : "transparent",
															color: active ? "white" : "rgba(255,255,255,0.8)",
															transition: "all 0.15s ease",
															"&:hover": {
																bgcolor: active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.1)",
															},
														}}
													>
														<item.icon sx={{ fontSize: 20 }} />
														<Typography
															variant="body2"
															sx={{
																flexGrow: 1,
																fontWeight: active ? 600 : 400,
																fontSize: "0.875rem",
															}}
														>
															{item.label}
														</Typography>
														{badgeFor(item) > 0 && (
															<Box
																sx={{
																	minWidth: 20,
																	height: 20,
																	px: 0.75,
																	borderRadius: "10px",
																	bgcolor: "#f57c00",
																	color: "white",
																	fontSize: "0.7rem",
																	fontWeight: 700,
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																}}
															>
																{badgeFor(item)}
															</Box>
														)}
													</Box>
												</Link>
											);
										})}
									</Box>
								</Collapse>
							</Box>
						);
					})
				)}
			</Box>

			{/* Footer */}
			{!collapsed && (
				<>
					<Divider sx={{ bgcolor: "rgba(255,255,255,0.15)", mx: 2 }} />
					<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
						<UserComponent variant="sidebar" />
						<Typography
							variant="caption"
							sx={{
								color: "rgba(255,255,255,0.4)",
								display: "block",
								textAlign: "center",
								fontSize: "0.65rem",
							}}
						>
							© 2025 Sisagri
						</Typography>
					</Box>
				</>
			)}
		</Box>
	);
}

export default AppSidebar;
